import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, Btn, Field, Input, Select, Th, fmtN, useMasters, MasterOptions } from "./camCommon";

export default function CamInventory() {
  const { byKind } = useMasters();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ location: "", commodity: "", opening: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/cam/inventory").then((r) => setRows(r.data.rows || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/cam/stocks", form);
      toast.success("Opening stock saved");
      setForm({ location: "", commodity: "", opening: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const totalValue = rows.reduce((s, r) => s + (r.valuation || 0), 0);

  return (
    <div className="space-y-4" data-testid="cam-inventory">
      <Card title="Set Opening Stock" subtitle="Book the physical opening balance per tank/terminal — receipts & deliveries then roll forward automatically" testid="cam-opening-card">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <Field label="Location *">
            <Select value={form.location} onChange={set("location")} data-testid="stock-location">
              <MasterOptions items={byKind("location")} />
            </Select>
          </Field>
          <Field label="Commodity *">
            <Select value={form.commodity} onChange={set("commodity")} data-testid="stock-commodity">
              <MasterOptions items={byKind("commodity")} />
            </Select>
          </Field>
          <Field label="Opening Volume *">
            <Input type="number" min="0" value={form.opening} onChange={set("opening")} placeholder="e.g. 120000" data-testid="stock-opening" />
          </Field>
          <Btn onClick={save} disabled={saving} data-testid="stock-save-btn" className="h-[34px]">
            {saving ? "Saving…" : "Save Opening"}
          </Btn>
        </div>
      </Card>

      <Card title="Inventory Positions" subtitle="Opening + receipts − deliveries = closing, valued at current market price" testid="cam-inventory-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <Th>Location</Th><Th>Commodity</Th><Th>UoM</Th>
                <Th className="text-right">Opening</Th><Th className="text-right">Receipts</Th>
                <Th className="text-right">Deliveries</Th><Th className="text-right">Closing</Th>
                <Th className="text-right">Mkt Price</Th><Th className="text-right">Valuation</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.location}-${r.commodity}`} className="border-b border-slate-100 hover:bg-slate-50/60"
                  data-testid={`inv-row-${r.location}-${r.commodity}`}>
                  <td className="py-2 pr-3 font-semibold text-[#0B1E3F]">{r.location}</td>
                  <td className="py-2 pr-3 font-semibold">{r.commodity}</td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{r.uom}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{fmtN(r.opening)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-emerald-700">{r.receipts ? `+${fmtN(r.receipts)}` : "—"}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-red-600">{r.deliveries ? `−${fmtN(r.deliveries)}` : "—"}</td>
                  <td className={`py-2 pr-3 text-right tabular-nums font-semibold ${r.closing < 0 ? "text-red-600" : ""}`}>{fmtN(r.closing)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-slate-500">{fmtN(r.market_price, 2)}</td>
                  <td className="py-2 text-right tabular-nums font-semibold">${fmtN(r.valuation)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-slate-400 text-xs" data-testid="inv-empty">
                  No inventory — set opening stock or record delivered movements.</td></tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-300">
                  <td colSpan={8} className="py-2.5 pr-3 text-right text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">Total Inventory Value</td>
                  <td className="py-2.5 text-right tabular-nums font-bold text-[#0B1E3F]" data-testid="inv-total-value">${fmtN(totalValue)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}
